/* ==========================================================================
   Harper Reborn — motion + small behaviours for harper-* sections
   - scroll reveal            [data-hp-reveal]
   - counters                 [data-hp-count][data-to][data-dec]
   - rings                    [data-hp-ring][data-pct] (svg) / [data-hp-conic][data-pct]
   - tabs                     [data-hp-tabs] > [role=tab][aria-controls] (+ [data-hp-tabs-slider] swipe mode)
   - click-to-play video      [data-hp-video]
   - marquee fill             [data-hp-marquee] (clones items until 2× viewport)
   - sticky add-to-cart bar   [data-hp-sticky-atc] (listens to hp:qb:change from the
                              quantity-breaks block to show the tier total)
   - scroll-to-top            [data-hp-totop]
   - before/after slider      [data-hp-compare] (--hp-ba-pos, pointer drag + range input)
   - promo popup              [data-hp-popup] (delay, once per N days, editor-aware)
   - ambient video            [data-hp-ambient] video (plays only while on screen)
   - click-to-copy            [data-hp-copy] (swaps the label with data-hp-copied)
   Everything respects prefers-reduced-motion and re-initialises on
   shopify:section:load so the theme editor stays live.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('hp-js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;

  /* ---------------- reveal ---------------- */
  var revealIO = supportsIO
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            enter(e.target);
            revealIO.unobserve(e.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.02 }
      )
    : null;

  function enter(el) {
    el.classList.add('is-in');
    runCounters(el);
    runRings(el);
  }

  function initReveal(scope) {
    var els = scope.querySelectorAll('[data-hp-reveal]');
    var vh = window.innerHeight || 800;
    els.forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      var r = el.getBoundingClientRect();
      // Anything already on screen at init is shown immediately (no pop-in).
      if (reduce || !revealIO || r.top < vh * 0.92) {
        enter(el);
      } else {
        revealIO.observe(el);
      }
    });
  }

  /* ---------------- counters ---------------- */
  function easeOut(p) {
    return 1 - Math.pow(1 - p, 3);
  }

  function formatNumber(n, dec) {
    var s = n.toFixed(dec);
    // Turkish formatting: decimal comma, dot thousands.
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
  }

  function runCounters(scope) {
    var nodes = scope.matches && scope.matches('[data-hp-count]') ? [scope] : [];
    nodes = nodes.concat([].slice.call(scope.querySelectorAll('[data-hp-count]')));
    nodes.forEach(function (n) {
      if (n.dataset.hpDone) return;
      n.dataset.hpDone = '1';
      var to = parseFloat(String(n.dataset.to || '0').replace(',', '.'));
      var dec = parseInt(n.dataset.dec || '0', 10);
      if (isNaN(to)) return;
      if (reduce) {
        n.textContent = formatNumber(to, dec);
        return;
      }
      var t0 = null;
      var dur = 1400;
      function step(t) {
        if (!t0) t0 = t;
        var p = Math.min(1, (t - t0) / dur);
        n.textContent = formatNumber(to * easeOut(p), dec);
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ---------------- rings ---------------- */
  function runRings(scope) {
    scope.querySelectorAll('[data-hp-ring]').forEach(function (c) {
      if (c.dataset.hpDone) return;
      c.dataset.hpDone = '1';
      var pct = Math.max(0, Math.min(100, parseFloat(c.dataset.pct || '0')));
      var r = parseFloat(c.getAttribute('r') || '26');
      var len = 2 * Math.PI * r;
      c.style.strokeDasharray = len;
      c.style.strokeDashoffset = len;
      var target = len * (1 - pct / 100);
      if (reduce) {
        c.style.transition = 'none';
        c.style.strokeDashoffset = target;
        return;
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          c.style.strokeDashoffset = target;
        });
      });
    });

    scope.querySelectorAll('[data-hp-conic]').forEach(function (r) {
      if (r.dataset.hpDone) return;
      r.dataset.hpDone = '1';
      var p = Math.max(0, Math.min(100, parseFloat(r.dataset.pct || '0')));
      if (reduce) {
        r.style.setProperty('--p', p + '%');
        return;
      }
      var c = 0;
      var iv = setInterval(function () {
        c += 2;
        r.style.setProperty('--p', Math.min(c, p) + '%');
        if (c >= p) clearInterval(iv);
      }, 18);
    });
  }

  /* ---------------- tabs (+ optional swipe-strip mode) ----------------
     [data-hp-tabs] wraps the [role=tab] buttons AND their panels.
     data-hp-tabs-slider="(max-width: 749px)" turns on strip mode while that
     media query matches: panels inside [data-hp-tabs-panels] are all shown
     side by side (CSS makes the wrapper a snap-scrolling row), tapping a tab
     scrolls to its panel, and swiping highlights the matching tab. Outside
     the query it is a normal tab set: one panel shown, the rest hidden. */
  function initTabs(scope) {
    scope.querySelectorAll('[data-hp-tabs]').forEach(function (group) {
      if (group.dataset.hpBound) return;
      group.dataset.hpBound = '1';
      var tabs = [].slice.call(group.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      var panels = tabs.map(function (t) {
        return doc.getElementById(t.getAttribute('aria-controls'));
      });
      var strip = group.querySelector('[data-hp-tabs-panels]');
      var mq = group.dataset.hpTabsSlider && window.matchMedia ? window.matchMedia(group.dataset.hpTabsSlider) : null;
      var slider = false;
      var io = null;
      var current = null;
      var lockUntil = 0; // ignore scroll-driven selection while we scroll programmatically
      // Videos only get a src (= start downloading) once the group is on screen; the
      // theme editor re-renders sections often and 1080p sources add up fast otherwise.
      var inView = !supportsIO;

      function playOnly(panel) {
        panels.forEach(function (p) {
          if (!p) return;
          p.querySelectorAll('video').forEach(function (v) {
            if (p === panel && inView) {
              if (v.dataset.src && !v.getAttribute('src')) v.setAttribute('src', v.dataset.src);
              var pr = v.play();
              if (pr && pr.catch) pr.catch(function () {});
            } else {
              v.pause();
            }
          });
        });
      }

      if (supportsIO) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              inView = e.isIntersecting;
              playOnly(inView && current ? panels[tabs.indexOf(current)] : null);
            });
          },
          { threshold: 0.15 }
        ).observe(group);
      }

      function centerIn(list, el) {
        if (!list || !el || list.scrollWidth <= list.clientWidth) return;
        var lr = list.getBoundingClientRect();
        var er = el.getBoundingClientRect();
        var left = list.scrollLeft + (er.left - lr.left) - (list.clientWidth - er.width) / 2;
        list.scrollTo({ left: Math.max(0, left), behavior: reduce ? 'auto' : 'smooth' });
      }

      function select(tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        current = tab;
        centerIn(tab.parentElement, tab); // keep the active pill visible in a scrolling tablist
      }

      function activate(tab, scroll) {
        select(tab);
        var panel = panels[tabs.indexOf(tab)];
        if (slider) {
          if (panel && strip && scroll !== false) {
            lockUntil = Date.now() + 800;
            var sr = strip.getBoundingClientRect();
            var pr = panel.getBoundingClientRect();
            var pad = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
            strip.scrollTo({ left: strip.scrollLeft + (pr.left - sr.left) - pad, behavior: reduce ? 'auto' : 'smooth' });
          }
        } else {
          panels.forEach(function (p, i) {
            if (p) p.hidden = tabs[i] !== tab;
          });
        }
        playOnly(panel);
      }

      function setMode() {
        slider = !!(mq && mq.matches && strip);
        if (io) {
          io.disconnect();
          io = null;
        }
        if (slider) {
          panels.forEach(function (p) {
            if (p) p.hidden = false;
          });
          if (supportsIO) {
            io = new IntersectionObserver(
              function (entries) {
                if (Date.now() < lockUntil) return;
                entries.forEach(function (e) {
                  if (!e.isIntersecting) return;
                  var i = panels.indexOf(e.target);
                  if (i > -1 && tabs[i] !== current) {
                    select(tabs[i]);
                    playOnly(e.target);
                  }
                });
              },
              { root: strip, threshold: 0.6 }
            );
            panels.forEach(function (p) {
              if (p) io.observe(p);
            });
          }
        }
        activate(current || tabs[0], false);
      }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () {
          activate(t);
        });
        t.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          next.focus();
          activate(next);
        });
      });

      current = tabs.filter(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      })[0] || tabs[0];
      setMode();
      if (mq) {
        if (mq.addEventListener) mq.addEventListener('change', setMode);
        else if (mq.addListener) mq.addListener(setMode);
      }
      group.hpActivate = activate; // used by the theme-editor block:select hook
    });
  }

  /* ---------------- click-to-play video ---------------- */
  function initVideos(scope) {
    scope.querySelectorAll('[data-hp-video]').forEach(function (wrap) {
      if (wrap.dataset.hpBound) return;
      wrap.dataset.hpBound = '1';
      var video = wrap.querySelector('video');
      if (!video) return;
      function toggle() {
        if (video.paused) {
          // Only one proof video plays at a time.
          doc.querySelectorAll('[data-hp-video] video').forEach(function (v) {
            if (v !== video) {
              v.pause();
              v.closest('[data-hp-video]').classList.remove('is-playing');
            }
          });
          video.muted = false;
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
      }
      wrap.addEventListener('click', function (e) {
        e.preventDefault();
        toggle();
      });
      wrap.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      });
      video.addEventListener('play', function () {
        wrap.classList.add('is-playing');
      });
      video.addEventListener('pause', function () {
        wrap.classList.remove('is-playing');
      });
      video.addEventListener('ended', function () {
        wrap.classList.remove('is-playing');
      });
    });
  }

  /* ---------------- marquee fill ---------------- */
  function initMarquee(scope) {
    scope.querySelectorAll('[data-hp-marquee]').forEach(function (m) {
      var track = m.querySelector('.hp-marquee-track');
      if (!track) return;
      var seqs = [].slice.call(track.children);
      if (seqs.length < 2) return;
      var base = seqs[0];
      function fill() {
        var vw = m.clientWidth || window.innerWidth;
        var items = [].slice.call(base.children).filter(function (c) {
          return !c.dataset.hpClone;
        });
        // Drop earlier clones, then re-clone until each sequence covers the viewport.
        seqs.forEach(function (s) {
          [].slice.call(s.children).forEach(function (c) {
            if (c.dataset.hpClone) s.removeChild(c);
          });
        });
        var guard = 0;
        while (base.scrollWidth < vw + 10 && guard < 12 && items.length) {
          items.forEach(function (it) {
            seqs.forEach(function (s) {
              var cl = it.cloneNode(true);
              cl.dataset.hpClone = '1';
              cl.setAttribute('aria-hidden', 'true');
              s.appendChild(cl);
            });
          });
          guard++;
        }
      }
      fill();
      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(fill, 160);
      });
      doc.addEventListener('visibilitychange', function () {
        m.classList.toggle('hp-marquee-paused', doc.hidden);
      });
    });
  }

  /* ---------------- sticky add-to-cart ---------------- */
  function initStickyAtc(scope) {
    scope.querySelectorAll('[data-hp-sticky-atc]').forEach(function (bar) {
      if (bar.dataset.hpBound) return;
      bar.dataset.hpBound = '1';
      var sectionId = bar.dataset.section;
      var anchor = doc.getElementById('ProductSubmitButton-' + sectionId);
      var btn = bar.querySelector('[data-hp-sticky-submit]');
      var priceOut = bar.querySelector('[data-hp-sticky-price]');
      var variantOut = bar.querySelector('[data-hp-sticky-variant]');
      if (!anchor || !btn) return;

      function syncPrice() {
        var priceEl = doc.getElementById('price-' + sectionId);
        if (!priceEl || !priceOut || bar.dataset.hpQb) return;
        var price = priceEl.querySelector('.price');
        var node = null;
        if (price && price.classList.contains('price--on-sale')) {
          node = price.querySelector('.price__sale .price-item--sale');
        }
        if (!node) node = priceEl.querySelector('.price__regular .price-item--regular');
        if (node) priceOut.textContent = node.textContent.trim();
      }

      function syncVariant(variant) {
        if (!variantOut) return;
        if (variant && variant.title && variant.title !== 'Default Title') {
          variantOut.textContent = variant.title.replace(/\s*\/\s*/g, ' · ');
          variantOut.hidden = false;
        } else {
          variantOut.hidden = true;
        }
        var main = anchor;
        btn.disabled = !!(main.hasAttribute('disabled') || main.getAttribute('aria-disabled') === 'true');
        var label = main.querySelector('span');
        if (label) btn.querySelector('span').textContent = label.textContent.trim();
      }

      btn.addEventListener('click', function () {
        anchor.click();
      });

      // Quantity-breaks block: while a tier is selected the bar shows the tier
      // total instead of the unit price (see assets/hp-quantity-breaks.js).
      doc.addEventListener('hp:qb:change', function (e) {
        if (!e.target || e.target.dataset.section !== sectionId || !priceOut) return;
        var d = e.detail || {};
        if (d.totalText) {
          bar.dataset.hpQb = '1';
          priceOut.textContent = d.totalText;
        } else {
          delete bar.dataset.hpQb;
          syncPrice();
        }
      });

      if (supportsIO) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              var below = e.boundingClientRect.top < 0; // scrolled past the real button
              bar.classList.toggle('is-visible', !e.isIntersecting && below);
            });
          },
          { threshold: 0 }
        );
        io.observe(anchor);
      }

      // PUB_SUB_EVENTS is a top-level `const` in constants.js (a global binding, not a window property).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          var variant = event && event.data ? event.data.variant : null;
          requestAnimationFrame(function () {
            syncPrice();
            syncVariant(variant);
          });
        });
      }
      syncPrice();
      syncVariant(null);
    });
  }

  /* ---------------- scroll-to-top ---------------- */
  function initToTop() {
    var btn = doc.querySelector('[data-hp-totop]');
    if (!btn || btn.dataset.hpBound) return;
    btn.dataset.hpBound = '1';
    btn.hidden = false;
    var ticking = false;
    function check() {
      btn.classList.toggle('is-visible', window.scrollY > (window.innerHeight || 800) * 0.8);
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(check);
        }
      },
      { passive: true }
    );
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    check();
  }

  /* ---------------- before / after slider ----------------
     [data-hp-compare] root exposes --hp-ba-pos (0–100%). Dragging anywhere on
     the root moves the divider; the <input type=range> inside keeps it
     keyboard-accessible. */
  function initCompare(scope) {
    scope.querySelectorAll('[data-hp-compare]').forEach(function (root) {
      if (root.dataset.hpBound) return;
      root.dataset.hpBound = '1';
      var range = root.querySelector('input[type="range"]');
      var dragging = false;

      function set(pct) {
        pct = Math.max(0, Math.min(100, pct));
        root.style.setProperty('--hp-ba-pos', pct + '%');
        if (range && Math.round(parseFloat(range.value)) !== Math.round(pct)) range.value = Math.round(pct);
      }
      function fromEvent(e) {
        var r = root.getBoundingClientRect();
        if (!r.width) return;
        set(((e.clientX - r.left) / r.width) * 100);
      }

      root.addEventListener('pointerdown', function (e) {
        if (e.target === range) return;
        dragging = true;
        root.classList.add('is-dragging');
        if (root.setPointerCapture) root.setPointerCapture(e.pointerId);
        fromEvent(e);
        e.preventDefault();
      });
      root.addEventListener('pointermove', function (e) {
        if (dragging) fromEvent(e);
      });
      function end() {
        dragging = false;
        root.classList.remove('is-dragging');
      }
      root.addEventListener('pointerup', end);
      root.addEventListener('pointercancel', end);
      root.addEventListener('lostpointercapture', end);
      if (range) {
        range.addEventListener('input', function () {
          set(parseFloat(range.value));
        });
      }
      set(parseFloat(root.dataset.hpCompare || (range && range.value) || '50'));
    });
  }

  /* ---------------- promo popup ----------------
     [data-hp-popup] data-hp-popup-delay="5" data-hp-popup-days="7"
     data-hp-popup-key="…" [data-hp-popup-open] (force open, e.g. after a form post).
     Inside: [data-hp-popup-close] buttons and a .hp-popup__bg backdrop. */
  function initPopup(scope) {
    scope.querySelectorAll('[data-hp-popup]').forEach(function (pop) {
      if (pop.dataset.hpBound) return;
      pop.dataset.hpBound = '1';
      var key = pop.dataset.hpPopupKey || 'hp-popup';
      var delay = parseFloat(pop.dataset.hpPopupDelay || '5') * 1000;
      var days = parseFloat(pop.dataset.hpPopupDays || '7');
      var design = !!(window.Shopify && window.Shopify.designMode);
      var lastFocus = null;
      var timer = null;

      function seen() {
        try {
          var t = parseInt(window.localStorage.getItem(key) || '0', 10);
          return t && Date.now() - t < days * 864e5;
        } catch (e) {
          return false;
        }
      }
      function remember() {
        try {
          window.localStorage.setItem(key, String(Date.now()));
        } catch (e) { /* ignore */ }
      }
      function open() {
        if (!pop.hidden) return;
        lastFocus = doc.activeElement;
        pop.hidden = false;
        doc.body.classList.add('hp-popup-open');
        requestAnimationFrame(function () {
          pop.classList.add('is-open');
          var first = pop.querySelector('input, button, a');
          if (first) first.focus();
        });
      }
      function close(persist) {
        if (pop.hidden) return;
        pop.classList.remove('is-open');
        doc.body.classList.remove('hp-popup-open');
        if (persist !== false && !design) remember();
        setTimeout(function () {
          pop.hidden = true;
        }, reduce ? 0 : 260);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }

      pop.querySelectorAll('[data-hp-popup-close], .hp-popup__bg').forEach(function (b) {
        b.addEventListener('click', function () {
          close();
        });
      });
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !pop.hidden) close();
      });

      if (pop.hasAttribute('data-hp-popup-open')) {
        open();
      } else if (design) {
        // In the editor, only open when the merchant selects the section.
        var sec = pop.closest('.shopify-section');
        doc.addEventListener('shopify:section:select', function (e) {
          if (e.target === sec) open();
        });
        doc.addEventListener('shopify:section:deselect', function (e) {
          if (e.target === sec) close(false);
        });
      } else if (!seen()) {
        timer = setTimeout(open, delay);
      }
      pop.hpOpen = open;
      pop.hpClose = close;
    });
  }

  /* ---------------- ambient (muted autoplay) video ---------------- */
  function initAmbient(scope) {
    scope.querySelectorAll('[data-hp-ambient] video').forEach(function (v) {
      if (v.dataset.hpBound) return;
      v.dataset.hpBound = '1';
      v.muted = true;
      v.setAttribute('playsinline', '');
      if (reduce) {
        v.removeAttribute('autoplay');
        v.pause();
        return;
      }
      if (!supportsIO) return;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.15 }
      );
      io.observe(v);
    });
  }

  /* ---------------- click-to-copy ---------------- */
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return Promise.resolve(legacyCopy(text));
  }
  function legacyCopy(text) {
    var ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    doc.body.appendChild(ta);
    ta.select();
    try {
      doc.execCommand('copy');
    } catch (e) { /* ignore */ }
    doc.body.removeChild(ta);
  }
  function initCopy(scope) {
    scope.querySelectorAll('[data-hp-copy]').forEach(function (btn) {
      if (btn.dataset.hpBound) return;
      btn.dataset.hpBound = '1';
      var label = btn.querySelector('[data-hp-copy-label]') || btn;
      var original = label.textContent;
      btn.addEventListener('click', function () {
        copyText(btn.dataset.hpCopy || '').then(function () {
          btn.classList.add('is-copied');
          label.textContent = btn.dataset.hpCopied || 'Copied ✓';
          setTimeout(function () {
            btn.classList.remove('is-copied');
            label.textContent = original;
          }, 1800);
        });
      });
    });
  }

  /* ---------------- boot ---------------- */
  function init(scope) {
    scope = scope || doc;
    initMarquee(scope);
    initTabs(scope);
    initVideos(scope);
    initStickyAtc(scope);
    initCompare(scope);
    initPopup(scope);
    initAmbient(scope);
    initCopy(scope);
    initToTop();
    initReveal(scope);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () {
      init(doc);
    });
  } else {
    init(doc);
  }

  doc.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  // Theme editor: when a block is picked in the sidebar, make sure it is actually visible.
  doc.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (!el || !el.hasAttribute) return;
    if (el.hasAttribute('data-hp-reveal')) enter(el);
    var revealParent = el.closest('[data-hp-reveal]');
    if (revealParent) enter(revealParent);
    // A tab block → switch to its video.
    if (el.getAttribute('role') === 'tab') {
      var group = el.closest('[data-hp-tabs]');
      if (group && group.hpActivate) group.hpActivate(el);
    }
    // An FAQ question → open it.
    if (el.tagName === 'DETAILS') el.open = true;
  });

  window.HarperMotion = { init: init, copyText: copyText };
})();
